import { CAPTURE_MAX_EDGE_PX, UPLOAD_CHUNK_BYTES } from '@monrdv/shared';
import { db, type PendingMedia } from './localdb.js';
import { apiFetch, apiJson, ApiError } from './api.js';
import { notify } from './outbox.js';

/**
 * Captures: from the camera to the office.
 *
 * Three jobs, in order, and each of them exists because of something that happens in a van:
 *
 *   1. **Downscale before storing.** A 4 MB phone photograph becomes roughly 400 KB at a
 *      2048 px long edge, which is what the office needs and what a day of before/after shots
 *      has to fit into on a phone that may not see a network until six o'clock.
 *   2. **Checksum on the device.** The bytes are hashed before the first one leaves. A file
 *      assembled from chunks over a flaky radio is exactly the thing that arrives subtly
 *      wrong, and a corrupt photograph filed as evidence is worse than no photograph.
 *   3. **Upload in resumable chunks.** One bar of LTE in a stairwell drops halfway through.
 *      The server holds an offset; the client asks what it is and continues from there.
 */

/** Read back as an object URL for the gallery, and revoked by the caller when done. */
export function previewUrl(media: PendingMedia): string {
  return URL.createObjectURL(media.blob);
}

/**
 * Shrink a photograph to what the office actually needs.
 *
 * `createImageBitmap` and a canvas rather than a library: every browser the technicians carry
 * has both, and a dependency that decodes JPEG in JavaScript would be slower than the camera.
 * The original is not kept — nobody has ever asked for the 4 MB version, and keeping it would
 * fill the phone by Thursday.
 */
export async function downscale(file: Blob, maxEdge = CAPTURE_MAX_EDGE_PX): Promise<Blob> {
  if (!file.type.startsWith('image/')) return file;

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    // A format the browser will not decode — an HEIC from an iPhone, most likely. Sent as it
    // is rather than refused: the server can hold bytes it cannot resize.
    return file;
  }

  const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
  if (scale >= 1) {
    bitmap.close();
    return file;
  }

  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) {
    bitmap.close();
    return file;
  }
  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const resized = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', 0.82),
  );
  return resized ?? file;
}

/** SHA-256 of a blob, as hex. The identity the server checks the bytes against. */
export async function checksum(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export type CaptureInput = {
  appointmentId: string;
  kind: string;
  blob: Blob;
  caption?: string | null;
  position?: { latitude: number; longitude: number; accuracyM: number | null } | null;
  durationSeconds?: number | null;
  assetId?: string | null;
};

/**
 * Take a capture.
 *
 * Written to the phone and returned immediately — there is no request on this path, because
 * the technician is standing in front of the thing they photographed and must not wait for a
 * radio to agree.
 */
export async function capture(input: CaptureInput): Promise<PendingMedia> {
  const blob = await downscale(input.blob);
  const digest = await checksum(blob);
  const extension = blob.type === 'image/jpeg' ? 'jpg' : (blob.type.split('/')[1] ?? 'bin').replace(/[^a-z0-9]/g, '');

  const media: PendingMedia = {
    id: crypto.randomUUID(),
    appointmentId: input.appointmentId,
    kind: input.kind,
    blob,
    contentType: blob.type || 'application/octet-stream',
    fileName: `${input.kind}-${Date.now()}.${extension}`,
    checksum: digest,
    caption: input.caption ?? null,
    capturedAt: new Date().toISOString(),
    position: input.position ?? null,
    durationSeconds: input.durationSeconds ?? null,
    assetId: input.assetId ?? null,
    uploadId: null,
    offset: 0,
    attachmentId: null,
    attempts: 0,
    lastAttemptAt: null,
    rejectedReason: null,
    createdAt: new Date().toISOString(),
  };

  await (await db()).put('media', media);
  notify();
  return media;
}

/** The real attachment id for a capture, once the server has all of it. */
export async function attachmentIdFor(mediaId: string): Promise<string | null> {
  const media = await (await db()).get('media', mediaId);
  return media?.attachmentId ?? null;
}

/**
 * Push whatever bytes are waiting.
 *
 * One at a time and oldest first, for the same reason the outbox drains serially: four
 * concurrent uploads on one bar of LTE is slower than one, not faster.
 *
 * A finished capture keeps its blob until the next sync confirms the office has it — the
 * server's own copy comes back in the working set, and only then is the local one released.
 */
export async function uploadPending(): Promise<{ uploaded: number; failed: number; rejected: number }> {
  const database = await db();
  const queue = (await database.getAll('media'))
    .filter((m) => !m.attachmentId && !m.rejectedReason)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));

  const result = { uploaded: 0, failed: 0, rejected: 0 };

  for (const media of queue) {
    // A capture that has failed several times waits, so a bad one does not consume the whole
    // window every time the radio returns.
    const wait = Math.min(2 ** media.attempts * 2_000, 15 * 60_000);
    if (media.lastAttemptAt && Date.now() - new Date(media.lastAttemptAt).getTime() < wait) continue;

    try {
      const finished = await uploadOne(media);
      if (finished) result.uploaded += 1;
      else result.failed += 1;
    } catch (error) {
      const permanent = error instanceof ApiError && error.permanent;
      await database.put('media', {
        ...media,
        attempts: media.attempts + 1,
        lastAttemptAt: new Date().toISOString(),
        rejectedReason: permanent ? (error as ApiError).message : media.attempts + 1 >= 8 ? 'Envoi refusé après plusieurs tentatives.' : null,
      });
      if (permanent || media.attempts + 1 >= 8) result.rejected += 1;
      else result.failed += 1;
    }
    notify();
  }

  return result;
}

async function uploadOne(media: PendingMedia): Promise<boolean> {
  const database = await db();
  let state = media;

  /*
   * Start, or resume.
   *
   * The server keys the session on the checksum, so asking twice for the same bytes returns
   * the same session and the offset it already holds. That is what makes a lost reply
   * harmless: the client asks again and is told where it got to.
   */
  if (!state.uploadId) {
    const started = await apiJson<{ uploadId: string; offset: number; attachmentId: string | null }>(
      `/appointments/${state.appointmentId}/attachments`,
      {
        method: 'POST',
        body: JSON.stringify({
          kind: state.kind,
          fileName: state.fileName,
          contentType: state.contentType,
          byteSize: state.blob.size,
          checksum: state.checksum,
          caption: state.caption,
          capturedAt: state.capturedAt,
          position: state.position,
          durationSeconds: state.durationSeconds,
          assetId: state.assetId,
        }),
      },
    );
    state = { ...state, uploadId: started.uploadId, offset: started.offset, attachmentId: started.attachmentId };
    await database.put('media', state);
    if (state.attachmentId) return true;
  }

  while (state.offset < state.blob.size) {
    const end = Math.min(state.offset + UPLOAD_CHUNK_BYTES, state.blob.size);
    const chunk = state.blob.slice(state.offset, end);

    const response = await apiFetch(`/field/uploads/${state.uploadId}`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/octet-stream', 'upload-offset': String(state.offset) },
      body: chunk,
    });

    if (response.status === 409) {
      // The server holds a different amount than we thought — a chunk landed after the reply
      // was lost. It tells us where it actually is, and we continue from there.
      const body = (await response.json().catch(() => null)) as { offset?: number } | null;
      state = { ...state, offset: body?.offset ?? 0 };
      await database.put('media', state);
      continue;
    }

    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as { error?: string; message?: string } | null;
      throw new ApiError(response.status, body?.error ?? `http_${response.status}`, body?.message ?? `HTTP ${response.status}`);
    }

    const body = (await response.json()) as { offset: number; attachmentId: string | null };
    state = { ...state, offset: body.offset, attachmentId: body.attachmentId ?? state.attachmentId, attempts: 0 };
    await database.put('media', state);
    notify();

    if (state.attachmentId) return true;
  }

  return Boolean(state.attachmentId);
}

/**
 * Release a capture the office now holds.
 *
 * Called after a sync whose working set contains the attachment. Deliberately not called when
 * the upload merely *reports* success: the phone lets go of the only copy of a photograph
 * exactly once, and the server having said so in a round trip that may have been the last one
 * before the tunnel is not the same as the server having it.
 */
export async function releaseConfirmed(attachmentIds: Set<string>): Promise<number> {
  const database = await db();
  const all = await database.getAll('media');
  let released = 0;
  for (const media of all) {
    if (media.attachmentId && attachmentIds.has(media.attachmentId)) {
      await database.delete('media', media.id);
      released += 1;
    }
  }
  if (released) notify();
  return released;
}
